import {
  RCS_CONVERSATION_STATUS_ENDED,
  RCS_CONVERSATION_STATUS_ACTIVE,
  RCS_CONVERSATION_STATUS_TIMED_OUT,
} from '@rsos/constants/rcsStatuses';
import { baseQueryArgs, baseExtraOptions, rsosApi } from '../rsosApi';

const getRcsBaseEndpoint = orgName => {
  return `v1/rcs/psap/${orgName}`;
};

export const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;

/**
 * Extends the rsosApi for rcs endpoints.
 * Authorization header must be manually included to tell RTK which token to use
 * otherwise it will use the access token
 */
export const rcsApi = rsosApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Fetches a phone number's ongoing conversation metadata.
     * Returns 204 if none exists
     * @param {String} callerID - phone number
     * @param {String} orgName - Name of authority the user belongs to
     */
    getRcsConversationMetadata: builder.query({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getRcsBaseEndpoint(orgName)}/session/${callerID}`,
        method: 'GET',
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Ends a conversation with the given phone number
     * @param {String} callerID - phone number
     * @param {String} orgName - Name of authority the user belongs to
     */
    endRcsConversation: builder.query({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getRcsBaseEndpoint(orgName)}/session/${callerID}`,
        method: 'PUT',
        body: {
          conversation_status: RCS_CONVERSATION_STATUS_ENDED,
        },
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    /**
     * Accepts a conversation with the given phone number
     * @param {String} callerID - phone number
     * @param {String} orgName - Name of authority the user belongs to
     */
    acceptRcsConversation: builder.query({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getRcsBaseEndpoint(orgName)}/session/${callerID}`,
        method: 'PUT',
        body: {
          conversation_status: RCS_CONVERSATION_STATUS_ACTIVE,
        },
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),

    /**
     * Times out a conversation with the given phone number
     * @param {String} callerID - phone number
     * @param {String} orgName - Name of authority the user belongs to
     */
    timeoutRcsConversation: builder.query({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getRcsBaseEndpoint(orgName)}/session/${callerID}`,
        method: 'PUT',
        body: {
          conversation_status: RCS_CONVERSATION_STATUS_TIMED_OUT,
        },
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),

    /**
     * Lets backend know that the PSAP is typing. Workaround for TalkJS functionality not working.
     * @param {String} callerID - phone number
     * @param {String} orgName - Name of authority the user belongs to
     */
    indicateTypingRcsConversation: builder.query({
      query: ({ callerID, orgName }) => ({
        ...baseQueryArgs,
        url: `${getRcsBaseEndpoint(orgName)}/session/${callerID}/typing`,
        method: 'POST',
      }),
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetRcsConversationMetadataQuery,
  useLazyGetRcsConversationMetadataQuery,
  useEndRcsConversationQuery,
  useLazyEndRcsConversationQuery,
  useAcceptRcsConversationQuery,
  useLazyAcceptRcsConversationQuery,
  useIndicateTypingRcsConversationQuery,
  useLazyIndicateTypingRcsConversationQuery,
  useTimeoutRcsConversationQuery,
  useLazyTimeoutRcsConversationQuery,
} = rcsApi;
